<template>
  <div class="filter-section">
    <button
      type="button"
      class="section-header"
      :aria-expanded="expanded"
      :aria-controls="sectionId"
      @click="$emit('toggle')"
    >
      <span class="section-title">{{ title }}</span>
      <div class="section-arrow" :class="{ expanded }" aria-hidden="true">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </button>

    <div v-if="expanded" :id="sectionId" class="section-content">
      <div
        v-for="option in options"
        :key="option.value"
        class="option-item"
        @click="onOptionRowClick(option.value, $event)"
      >
        <span class="option-label">{{ option.text }}</span>
        <label class="checkbox-container">
          <input
            type="checkbox"
            role="checkbox"
            :value="option.value"
            v-model="selectedOptions"
            @change="emitChange"
            class="filter-checkbox"
            :aria-label="option.text"
          />
          <span class="checkbox-visual" aria-hidden="true"></span>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  sectionId: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "change", "toggle"]);

const selectedOptions = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const emitChange = () => {
  emit("change", props.modelValue);
};

const isMobile = () => {
  return window.innerWidth <= 768;
};

const onOptionRowClick = (optionValue, event) => {
  if (!isMobile()) return;

  const target = event.target;

  if (target?.type === "checkbox" || target?.closest?.(".checkbox-container")) {
    return;
  }

  const nextSelectedOptions = [...props.modelValue];
  const optionIndex = nextSelectedOptions.indexOf(optionValue);

  if (optionIndex > -1) {
    nextSelectedOptions.splice(optionIndex, 1);
  } else {
    nextSelectedOptions.push(optionValue);
  }

  emit("update:modelValue", nextSelectedOptions);
  emit("change", nextSelectedOptions);
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 2px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

.section-header:focus-visible {
  outline: 3px solid
    var(--filter-section-focus-color, var(--filter-section-color, #00925e));
  outline-offset: 2px;
}

.section-header:hover {
  background-color: #f9fafb;
}

.section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--filter-section-color, var(--pink-dark-1));
  letter-spacing: 0.024px;
}

.section-arrow {
  color: var(--filter-section-color, var(--pink-dark-1));
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.section-arrow.expanded {
  transform: rotate(180deg);
}

.section-arrow svg {
  width: 18px;
  height: 18px;
}

.section-content {
  padding-left: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
  font-size: var(--fs-big);
}

.option-item:hover {
  background-color: #f9fafb;
}

.option-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--filter-section-color, var(--pink-dark-1));
  letter-spacing: 0.024px;
}

.checkbox-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.filter-checkbox {
  opacity: 0;
  position: absolute;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-visual {
  width: 24px;
  height: 24px;
  background-color: #ffffff;
  border: 2px solid #cccccc;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
}

.filter-checkbox:checked ~ .checkbox-visual,
.filter-checkbox:checked + .checkbox-visual {
  background-color: var(--filter-section-color, var(--pink-dark-1));
  border-color: var(--filter-section-color, var(--pink-dark-1));
}

.checkbox-visual:after {
  content: "";
  position: absolute;
  display: none;
}

.filter-checkbox:checked ~ .checkbox-visual:after,
.filter-checkbox:checked + .checkbox-visual:after {
  display: block;
}

.checkbox-visual:after {
  left: 7px;
  top: 3px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.filter-checkbox:disabled + .checkbox-visual {
  background-color: #f3f4f6;
  border-color: #e5e7eb;
  cursor: not-allowed;
}

.filter-checkbox:focus-visible + .checkbox-visual {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

@media (max-width: 768px) {
  .section-title,
  .option-label {
    font-size: var(--fs-small);
  }

  .option-item {
    cursor: pointer;
  }
}
</style>
